import axios, { AxiosInstance } from 'axios';
import { message } from 'antd';
import { getToken, removeToken } from '../utils/auth';
import { API_CONFIG } from '../config';

// 创建axios实例
const api: AxiosInstance = axios.create({
  baseURL: process.env.NODE_ENV === 'development' ? '/api/v1' : API_CONFIG.fullBaseURL,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const skipErrorHandler = (error.config as any)?.skipErrorHandler === true;
    if (skipErrorHandler) {
      return Promise.reject(error);
    }

    if (error.response) {
      // 登录和注册请求的401错误由组件自己处理
      const isAuthRequest = error.config.url?.includes('/auth/login') || 
                           error.config.url?.includes('/auth/register');
      
      switch (error.response.status) {
        case 401:
          // 只有非登录/注册请求才进行全局401处理
          if (!isAuthRequest) {
            message.error('登录已过期，请重新登录');
            removeToken();
            window.location.href = window.location.pathname.startsWith('/h5') ? '/h5/login' : '/login';
          }
          break;
        case 403:
          message.error('没有权限访问该资源');
          break;
        case 404:
          message.error('请求的资源不存在');
          break;
        case 500:
          message.error('服务器错误，请稍后重试');
          break;
        default:
          // 登录/注册请求的错误由组件处理，不显示全局消息
          if (!isAuthRequest) {
            message.error(error.response.data?.error || '请求失败');
          }
      }
    } else if (error.request) {
      message.error('网络错误，请检查您的网络连接');
    } else {
      message.error('请求失败，请稍后重试');
    }
    return Promise.reject(error);
  }
);

// 认证相关API
export const authAPI = {
  login: (data: { username: string; password: string }) =>
    api.post('/auth/login', data),
  register: (data: {
    username: string;
    password: string;
    name: string;
    class?: string;
    grade?: number;
    invitation_code: string;
  }) => api.post('/auth/register', data),
};

// 用户相关API
export const userAPI = {
  getProfile: () => api.get('/profile'),
  updateProfile: (data: { name?: string; class?: string; grade?: number }) =>
    api.put('/profile', data),
};

// 班级相关API
export const classAPI = {
  create: (data: { name: string; grade: number; description?: string }) =>
    api.post('/classes', data),
  list: (params?: { page?: number; page_size?: number; grade?: number }) =>
    api.get('/classes', { params }),
  update: (id: number, data: { name?: string; grade?: number; description?: string }) =>
    api.put(`/classes/${id}`, data),
  delete: (id: number) => api.delete(`/classes/${id}`),
};

// 学生相关API
export const studentAPI = {
  create: (data: { name: string; class_id: number; student_no?: string; email?: string; phone?: string }) =>
    api.post('/students', data),
  list: (params?: { page?: number; page_size?: number; class_id?: number; search?: string }) =>
    api.get('/students', { params }),
  get: (id: number) => api.get(`/students/${id}`),
  update: (id: number, data: any) => api.put(`/students/${id}`, data),
  delete: (id: number) => api.delete(`/students/${id}`),
  quickCreate: (data: { name: string; class_id: number; student_no?: string }) =>
    api.post('/students/quick-create', data),
  autocomplete: (q: string) => api.get('/students/autocomplete', { params: { q } }),
};

// 作文相关API
export const essayAPI = {
  createWithStudent: (data: {
    topic_id: number;
    essay_type?: string;
    student_id?: number;
    student_name?: string;
    class_id: number;
    student_no?: string;
  }) =>
    api.post('/essays/create-with-student', data),
  batchCreate: (data: {
    topic_id: number;
    essay_type?: string;
    student_ids: number[];
  }) =>
    api.post('/essays/batch', data),
  getBatch: (batchId: string) => api.get(`/essays/batch/${batchId}`),
  getBatchStatus: (batchId: string) => api.get(`/essays/batch/${batchId}/status`),
  batchOCR: (essayIds: number[]) => api.post('/essays/batch/ocr', { essay_ids: essayIds }),
  batchCorrect: (essayIds: number[]) => api.post('/essays/batch/correct', { essay_ids: essayIds }),
  list: (params?: { 
    page?: number; 
    page_size?: number; 
    status?: string;
    student_id?: number;
    student_name?: string;
    class_id?: number;
    class_name?: string;
    title?: string;
    score_band?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }) =>
    api.get('/essays', { params }),
  get: (id: number) => api.get(`/essays/${id}`),
  update: (
    id: number,
    data: {
      topic_id?: number;
      essay_type?: string;
      corrected_text?: string;
    }
  ) => api.put(`/essays/${id}`, data),
  delete: (id: number) => api.delete(`/essays/${id}`),
  upload: (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/essays/${id}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  uploadMultiple: (id: number, files: File[]) => {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`file_${index}`, file);
    });
    formData.append('count', files.length.toString());
    return api.post(`/essays/${id}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  ocr: (id: number) => api.post(`/essays/${id}/ocr`),
  correct: (id: number) => api.post(`/essays/${id}/correct`, {}, { 
    timeout: 5 * 60 * 1000  // AI批改专用超时设置：5分钟
  }),
  getTitles: (keyword?: string) => api.get('/essays/titles', { params: keyword ? { keyword } : {} }),
  getProgress: (id: number) => api.get(`/essays/${id}/progress`),
};

// 统计分析相关API
export const statisticsAPI = {
  teacherDashboard: () => api.get('/statistics/dashboard'),
  studentProgress: (studentId: number) => api.get(`/statistics/student/${studentId}/progress`),
  classStatistics: (classId: number) => api.get(`/statistics/class/${classId}`),
};

// 分析相关API
export const analysisAPI = {
  getStats: () => api.get('/analysis/stats'),
};

// 批改相关API
export const correctionAPI = {
  get: (essayId: number, config?: any) => api.get(`/corrections/${essayId}`, config),
};

// 题目相关API
export const topicAPI = {
  list: (params?: {
    page?: number;
    page_size?: number;
    keyword?: string;
    essay_type?: string;
    is_active?: boolean;
  }) => api.get('/topics', { params }),
  create: (data: {
    title: string;
    topic?: string;
    requirements?: string;
    essay_type?: string;
    word_limit?: number;
  }) => api.post('/topics', data),
  get: (id: number) => api.get(`/topics/${id}`),
  update: (id: number, data: {
    title?: string;
    topic?: string;
    requirements?: string;
    essay_type?: string;
    word_limit?: number;
    is_active?: boolean;
  }) => api.put(`/topics/${id}`, data),
  delete: (id: number) => api.delete(`/topics/${id}`),
  getEssays: (id: number, params?: { page?: number; page_size?: number }) =>
    api.get(`/topics/${id}/essays`, { params }),
  getStats: (id: number) => api.get(`/topics/${id}/stats`),
};

export default api;
